import { Button, Chip, IconButton, Spinner } from '@material-tailwind/react';
import { ContactIcon, EyeIcon, PencilIcon, PlusIcon, TrashIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useGetContractsByUserId } from '../../../custom-hooks/use-contracts';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import { useDeleteBooking } from '../../../custom-hooks/use-bookings';
import { InterpretActiveStatus } from '../../../utils/ui-interpreters';
import { convertToPresentableText } from '../../../utils/functions';
import moment from 'moment';
import { VERIFICATION_TYPES } from '../../../utils/enums';
import BookingFormModal from '../../booking/bookingFormModal';
import ContractFormModal from '../../contract/contractFormModal';
import { useNavigate } from 'react-router-dom';

const INITIAL_BOOKING_FORM_DATA = {
    // Basic Information
    user: '',
    contractType: 'permanent',
    startDate: '',
    endDate: '',
};

const INITIAL_CONTRACT_FORM_DATA = {
    // Basic Information
    user: '',
    contractType: 'permanent',
    startDate: '',
    endDate: '',
    status: VERIFICATION_TYPES.APPROVED,
    comment: '',
    reason: '',
    duration: 0,
    noticePeriod: 4,
    selectedOffices: [],
    selectedSeats: [],
    totalAdvanceRent: 0,
    securityDeposit: 0,
    oneTimeServiceCharges: 0,
    oneTimeOtherCharges: [],
};

const Contracts = ({ customer }) => {
    const [isContractFormModalOpen, setIsContractFormModalOpen] = useState(false);
    const [bookingIdToPerformAction, setBookingIdToPerformAction] = useState(null);
    const [bookingDataForModal, setBookingDataForModal] = useState(INITIAL_BOOKING_FORM_DATA);
    const [isBookingFormModalOpen, setIsBookingFormModalOpen] = useState(false);

    const { confirm, ConfirmationDialog } = useConfirm();
    const navigate = useNavigate();

    const deleteBookingMutation = useDeleteBooking();

    const {
        data: contracts,
        isLoading: isContractsLoading,
        error: contractsError,
        refetch: refetchContracts,
    } = useGetContractsByUserId(customer._id);



    const handleAddBookingFormModalOpen = (contract) => {
        setBookingIdToPerformAction(null);
        setBookingDataForModal({
            ...INITIAL_BOOKING_FORM_DATA,
            startDate: contract.startDate,
            contract: contract._id,
            user: customer._id
        });
        setIsBookingFormModalOpen(true);
    }

    const handleDeleteBooking = async (bookingId) => {
        const confirmation = await confirm({
            title: "Delete Booking",
            message: "Are you sure you want to delete this booking?",
            confirmText: "Delete",
            cancelText: "Cancel",
            confirmBtnColor: "red",
        });

        if (confirmation) {
            deleteBookingMutation.mutate(bookingId);
        }
    }

    const handleEditBookingFormModalOpen = (booking) => {
        setBookingIdToPerformAction(booking._id);
        setBookingDataForModal(booking);
        setIsBookingFormModalOpen(true);
    }

    const handleBookingFormModalClose = () => {
        setBookingIdToPerformAction(null);
        setBookingDataForModal(INITIAL_BOOKING_FORM_DATA);
        setIsBookingFormModalOpen(false);
    }

    return (
        <div className="border rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <ContactIcon size={16} />
                    Contracts
                </h2>
                <Button className="rounded-full border flex items-center gap-2" size="sm" disabled={!customer._id || isContractsLoading} variant="gradient" onClick={() => setIsContractFormModalOpen(true)}>
                    <PlusIcon size={16} />
                    Create New Contract
                </Button>
            </div>

            {isContractsLoading ? <Spinner /> :
                contractsError ? <div className="text-center py-8 text-red-500">Error loading contracts</div> :
                    (contracts?.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No contracts found for this customer
                        </div>
                    ) : (
                        <>
                            {contracts?.map((contract, index) => (
                                <div key={contract._id} className={` rounded-xl p-4 mb-4 ${getStatusColor(contract.status)}`}>
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-lg font-semibold">Contract #{index + 1}</h3>
                                            <p className="text-xs text-gray-600">ID: {contract._id}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {InterpretActiveStatus(contract.status)}
                                            <Chip color="blue" size="sm" variant="ghost" value={convertToPresentableText(contract.contractType)} />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        <div>
                                            <p className="text-sm text-gray-600">Start Date</p>
                                            <p className="font-medium">{moment(contract.startDate).format("MMM DD, YYYY")}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">End Date</p>
                                            <p className="font-medium">{contract.endDate ? moment(contract.endDate).format("MMM DD, YYYY") : "Open-ended"}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Total Advance Rent</p>
                                            <p className="font-medium">{formatCurrency(contract.totalAdvanceRent)}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Total Monthly Rent</p>
                                            <p className="font-medium">{formatCurrency(contract
                                                .bookings
                                                .filter((booking) => booking.status === VERIFICATION_TYPES.APPROVED)
                                                .reduce((acc, booking) => acc + booking.rentTotal, 0))}</p>
                                        </div>
                                    </div>

                                    {contract.reason && (
                                        <div className="mb-4">
                                            <p className="text-sm text-gray-600">Purpose</p>
                                            <p>{contract.reason}</p>
                                        </div>
                                    )}

                                    {contract.noticePeriod && (
                                        <div className="mb-4">
                                            <p className="text-sm text-gray-600">Notice Period</p>
                                            <p>{contract.noticePeriod} {parseInt(contract.noticePeriod) === 1 ? 'week' : 'weeks'}</p>
                                        </div>
                                    )}

                                    {/* Bookings Section */}
                                    <div className="mt-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="font-semibold">Bookings ({contract.bookings.length})</h4>
                                            {
                                                contract.status === VERIFICATION_TYPES.APPROVED && (
                                                    <IconButton className="rounded-full" size="sm" variant="outlined" onClick={() => handleAddBookingFormModalOpen(contract)}>
                                                        <PlusIcon size={12} />
                                                    </IconButton>
                                                )
                                            }
                                        </div>

                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rent</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                        {contract.status === VERIFICATION_TYPES.APPROVED && (
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {contract.bookings.map((booking) => (
                                                        <tr key={booking._id}>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {booking.office ? "Office" : "Seat"}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {booking.office ?
                                                                        `${booking.office.code} (${booking.office.capacity} seats)` :
                                                                        `${booking.seat.seatCode} (${booking.seat.seatType})`}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="text-sm text-gray-900">
                                                                    {booking.office ?
                                                                        `${booking.office.floor.building.name}, Floor ${booking.office.floor.floorCode}` :
                                                                        `${booking.seat.coworking.floor.building.name}, Floor ${booking.seat.coworking.floor.floorCode}`}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {booking.office ?
                                                                        booking.office.floor.building.branch.name :
                                                                        booking.seat.coworking.floor.building.branch.name} Branch
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                {booking.dailyStartTime && booking.dailyEndTime ? (
                                                                    <div>
                                                                        <div>Daily: {moment(booking.dailyStartTime).format("h:mm A")} - {moment(booking.dailyEndTime).format("h:mm A")}</div>
                                                                        <div className="text-xs">From: {moment(booking.startDate).format("MMM DD, YYYY")} - {booking.endDate ? moment(booking.endDate).format("MMM DD, YYYY") : `${contract.noticePeriod} weeks notice`}</div>
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                        <div>Full-time access</div>
                                                                        <div className="text-xs">From: {moment(booking.startDate).format("MMM DD, YYYY")} - {booking.endDate ? moment(booking.endDate).format("MMM DD, YYYY") : "Open"}</div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {formatCurrency(booking.rentTotal)}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {booking.rentFrequency}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(booking.status)}`}>
                                                                    {convertToPresentableText(booking.status)}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="flex items-center gap-1">

                                                                    <IconButton variant="gradient" color="blue" size="sm" className="rounded-full" onClick={() => navigate(`booking/${booking._id}`)}>
                                                                        <EyeIcon size={12} />
                                                                    </IconButton>
                                                                    {contract.status === VERIFICATION_TYPES.APPROVED && booking.status === VERIFICATION_TYPES.APPROVED && (
                                                                        <IconButton variant="gradient" color="orange" size="sm" className="rounded-full" onClick={() => handleEditBookingFormModalOpen(booking)}>
                                                                            <PencilIcon size={12} />
                                                                        </IconButton>
                                                                    )}
                                                                    {contract.status === VERIFICATION_TYPES.APPROVED && booking.status === VERIFICATION_TYPES.APPROVED && (
                                                                        <IconButton variant="gradient" color="red" size="sm" className="rounded-full" onClick={() => handleDeleteBooking(booking._id)}>
                                                                            <TrashIcon size={12} />
                                                                        </IconButton>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    ))}
            {/* Modals */}
            <ContractFormModal
                isOpen={isContractFormModalOpen}
                onClose={() => setIsContractFormModalOpen(false)}
                customer={customer}
                defaultData={INITIAL_CONTRACT_FORM_DATA}
            />
            {isBookingFormModalOpen && (
                <BookingFormModal
                    isOpen={isBookingFormModalOpen}
                    onClose={handleBookingFormModalClose}
                    defaultData={bookingDataForModal}
                    isUpdating={!!bookingIdToPerformAction}
                    updatingBookingId={bookingIdToPerformAction}
                    customerId={customer._id}
                    onSuccess={() => refetchContracts()}
                />
            )}
            <ConfirmationDialog />
        </div>
    );
};

// Helper functions
function getStatusColor(status) {
    switch (status) {
        case 'approved':
            return 'border-green-200 bg-green-50';
        case 'pending':
            return 'border-yellow-200 bg-yellow-50';
        case 'rejected':
            return 'border-red-200 bg-red-50';
        case 'cancelled':
            return 'border-gray-200 bg-gray-50';
        default:
            return 'border-gray-200';
    }
}

function getStatusBadgeColor(status) {
    switch (status) {
        case 'approved':
            return 'bg-green-100 text-green-800';
        case 'pending':
            return 'bg-yellow-100 text-yellow-800';
        case 'rejected':
            return 'bg-red-100 text-red-800';
        case 'cancelled':
            return 'bg-gray-100 text-gray-800';
        case 'completed':
            return 'bg-blue-100 text-blue-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
}

function formatCurrency(amount) {
    return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
}

export default Contracts;